import { ref, uploadString, uploadBytes, getDownloadURL } from "firebase/storage";
import { firebaseConfig, storage } from "../firebase";

export async function compressImage(dataUrl: string, maxWidth = 1400, maxHeight = 1400, quality = 0.82): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = dataUrl;
    img.onload = () => {
      let width = img.width;
      let height = img.height;
      if (width > height) {
        if (width > maxWidth) {
          height *= maxWidth / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width *= maxHeight / height;
          height = maxHeight;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(width);
      canvas.height = Math.round(height);
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error("Failed to get canvas context"));
        return;
      }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      let currentQuality = quality;
      let result = canvas.toDataURL('image/jpeg', currentQuality);

      // Ensure compressed image is strictly below 4.5 MB (comfortably below 5 MB Firebase Storage limit)
      while (result.length * 0.75 > 4.5 * 1024 * 1024 && currentQuality > 0.3) {
        currentQuality -= 0.15;
        result = canvas.toDataURL('image/jpeg', currentQuality);
      }

      if (result.length * 0.75 > 4.5 * 1024 * 1024) {
        canvas.width = Math.round(canvas.width * 0.7);
        canvas.height = Math.round(canvas.height * 0.7);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        result = canvas.toDataURL('image/jpeg', 0.7);
      }

      resolve(result);
    };
    img.onerror = (e) => reject(e);
  });
}

export function parseDate(value: any): Date | null {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
  if (typeof value.Seconds === "number") return new Date(value.Seconds * 1000);
  if (typeof value._seconds === "number") return new Date(value._seconds * 1000);
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function isMemberUnpaidOrExpired(profile: any): boolean {
  if (!profile) return true;
  if (profile.isPaid === false) return true;
  if (profile.membershipExpirationDate) {
    const expDate = parseDate(profile.membershipExpirationDate);
    if (expDate && expDate < new Date()) {
      return true;
    }
  }
  return false;
}

export function formatRelativeTime(dateValue: any): string {
  const date = parseDate(dateValue);
  if (!date) return "some time ago";
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function teamLabel(team: any[]): string {
  if (!Array.isArray(team) || team.length === 0) return "Unknown Team";
  return team.map((player) => player.displayName || "Unknown Member").join(" and ");
}

export function sanitizeFileName(fileName: string): string {
  return String(fileName ?? "upload").replace(/[^a-zA-Z0-9._-]/g, "-");
}

export async function uploadDataUrl(path: string, dataUrl: string): Promise<string> {
  if (!storage) throw new Error("Firebase Storage is not initialized.");
  const storageRef = ref(storage, path);

  let metadata = {};
  if (typeof dataUrl === 'string' && dataUrl.startsWith('data:')) {
    const parts = dataUrl.split(';');
    if (parts.length > 0) {
      const mimeType = parts[0].split(':')[1];
      metadata = { contentType: mimeType };
    }
  }

  await uploadString(storageRef, dataUrl, "data_url", metadata);
  return await getDownloadURL(storageRef);
}

export async function uploadBlob(path: string, blob: Blob, contentType?: string): Promise<string> {
  if (!storage) throw new Error("Firebase Storage is not initialized.");
  const storageRef = ref(storage, path);
  const metadata = contentType ? { contentType } : (blob.type ? { contentType: blob.type } : {});
  await uploadBytes(storageRef, blob, metadata);
  return await getDownloadURL(storageRef);
}

export function parseMediaUrls(raw?: string | null): string[] {
  if (!raw || typeof raw !== 'string') return [];
  const trimmed = raw.trim();
  if (!trimmed) return [];
  // If the entire string is a single data URI, return it directly so base64 comma is preserved
  if (trimmed.startsWith('data:')) {
    return [trimmed];
  }
  return trimmed
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
}

export function isVideoUrl(url: string | null | undefined): boolean {
  if (!url || typeof url !== 'string') return false;
  const clean = url.split('?')[0].toLowerCase();
  return clean.endsWith('.mp4') || 
         clean.endsWith('.webm') || 
         clean.endsWith('.mov') || 
         clean.endsWith('.ogg') || 
         clean.endsWith('.m4v') ||
         url.includes('data:video/') ||
         url.includes('/feed-videos/');
}

export async function uploadScorecardImage(identifier: string, dataUrl: string): Promise<string> {
  const cleanId = (identifier || `scorecard_${Date.now()}`).replace(/[^a-zA-Z0-9_-]/g, '_');
  return await uploadDataUrl(`scorecards/${cleanId}.jpg`, dataUrl);
}

/**
 * Robust helper to fix common photo URL issues:
 * 1. Converts relative 'Images/...' to absolute '/Images/...'
 * 2. Resolves 'gs://' URLs to HTTPS download URLs.
 * 3. Standardizes legacy 'appspot.com' bucket domain to 'firebasestorage.app'.
 * 4. Aggressively strips extensions from profile/gallery paths when they are stored extension-less.
 */
export function fixPhotoUrl(url: string | null | undefined): string {
  if (!url || typeof url !== 'string') return url || "";

  const lowerUrl = url.trim().toLowerCase();
  if (lowerUrl.startsWith('javascript:') || lowerUrl.startsWith('data:text/html') || lowerUrl.startsWith('vbscript:')) {
    return "";
  }

  if (url.startsWith('Images/')) return '/' + url;

  if (url.startsWith('gs://')) {
    const path = url.replace(/gs:\/\/[^\/]+\//, '');
    const encodedPath = encodeURIComponent(path).replace(/%2F/g, '/');
    return `https://firebasestorage.googleapis.com/v0/b/atlcardsharks-a9914.firebasestorage.app/o/${encodedPath}?alt=media`;
  }

  if (url.includes('/profile-photos/') || url.includes('/gallery-photos/') || url.includes('firebasestorage.googleapis.com')) {
    let fixed = url;

    if (fixed.includes('/profile-photo.') || fixed.includes('/slot-0.') || fixed.includes('/slot-1.')) {
      fixed = fixed.replace(/(\/profile-photo|\/slot-[01])\.[a-z0-9]+/i, '$1');
    }

    if (fixed.includes('atlcardsharks-a9914.appspot.com')) {
      fixed = fixed.replace('atlcardsharks-a9914.appspot.com', 'atlcardsharks-a9914.firebasestorage.app');
    }

    if (fixed.includes('firebasestorage.googleapis.com') && !fixed.includes('alt=media')) {
      fixed += (fixed.includes('?') ? '&' : '?') + 'alt=media';
    }

    return fixed;
  }

  return url;
}

export async function getStorageFileUrl(filePath: string | null | undefined): Promise<string | null> {
  if (!filePath) return null;
  try {
    const fileRef = ref(storage, filePath);
    return await getDownloadURL(fileRef);
  } catch (e) {
    return null;
  }
}

let mapsPromise: Promise<void> | null = null;
export function loadGoogleMaps(): Promise<void> {
  if (mapsPromise) return mapsPromise;
  if ((window as any).google && (window as any).google.maps && (window as any).google.maps.places) {
    mapsPromise = Promise.resolve();
    return mapsPromise;
  }
  mapsPromise = new Promise((resolve, reject) => {
    (window as any).initGoogleMapsCallback = () => resolve();
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${firebaseConfig.apiKey}&libraries=places&callback=initGoogleMapsCallback&loading=async`;
    script.async = true;
    script.defer = true;
    script.onerror = (err) => reject(err);
    document.head.appendChild(script);
  });
  return mapsPromise;
}
